import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Col,
  DatePicker,
  Descriptions,
  Input,
  InputNumber,
  Modal,
  Row,
  Statistic,
  Table,
  Tag,
} from "antd";
import { LockOutlined } from "@ant-design/icons";
import dayjs, { Dayjs } from "dayjs";
import adminApi from "../../../services/adminApi";
import { formatCurrency } from "../../../utils/reportExport";
import { PAYMENT_METHOD_LABELS } from "../reportCommon";

const DayClosePanel: React.FC = () => {
  const [date, setDate] = useState<Dayjs>(dayjs());
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [countedCash, setCountedCash] = useState<number | null>(null);
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await adminApi.getDayClose(date.format("YYYY-MM-DD"));
      setData(res.data.data);
      setCountedCash(null);
      setNotes("");
    } catch (e: any) {
      setError(e.response?.data?.message || "Failed to load day close preview");
    } finally {
      setLoading(false);
    }
  }, [date]);

  useEffect(() => {
    load();
  }, [load]);

  const summary = data?.summary || data || {};
  const closed = Boolean(data?.closure || data?.isClosed);
  const closure = data?.closure;
  const expectedCash =
    summary.expectedCash ?? summary.cashCollected ?? summary.cash ?? 0;

  const submit = () => {
    Modal.confirm({
      title: `Close day ${date.format("DD/MM/YYYY")}?`,
      content: "Bills of a closed day can no longer be edited or voided.",
      okText: "Close day",
      okButtonProps: { danger: true },
      onOk: async () => {
        setSubmitting(true);
        try {
          await adminApi.closeDay({
            date: date.format("YYYY-MM-DD"),
            countedCash: countedCash ?? undefined,
            notes: notes || undefined,
          });
          load();
        } catch {
          // API errors toasted by interceptor
        } finally {
          setSubmitting(false);
        }
      },
    });
  };

  const byMethod = summary.byMethod || summary.collectionsByMethod || [];

  return (
    <div>
      <Card
        size="small"
        title="Day Close"
        extra={
          <DatePicker
            size="small"
            value={date}
            allowClear={false}
            disabledDate={(d) => d.isAfter(dayjs(), "day")}
            onChange={(d) => d && setDate(d)}
          />
        }
        loading={loading}
      >
        {error && (
          <Alert type="error" showIcon message={error} style={{ marginBottom: 12 }} />
        )}

        {closed && (
          <Alert
            type="success"
            showIcon
            icon={<LockOutlined />}
            message={`Day ${date.format("DD/MM/YYYY")} is closed`}
            description={
              closure ? (
                <Descriptions size="small" column={2} style={{ marginTop: 8 }}>
                  <Descriptions.Item label="Closed by">
                    {closure.closedBy?.name || "—"}
                  </Descriptions.Item>
                  <Descriptions.Item label="Closed at">
                    {closure.closedAt
                      ? dayjs(closure.closedAt).format("DD/MM/YYYY HH:mm")
                      : "—"}
                  </Descriptions.Item>
                  <Descriptions.Item label="Counted cash">
                    {closure.countedCash != null
                      ? formatCurrency(closure.countedCash)
                      : "—"}
                  </Descriptions.Item>
                  <Descriptions.Item label="Notes">
                    {closure.notes || "—"}
                  </Descriptions.Item>
                </Descriptions>
              ) : undefined
            }
            style={{ marginBottom: 12 }}
          />
        )}

        <Row gutter={[12, 12]}>
          <Col xs={12} md={6}>
            <Statistic title="Bills" value={summary.billCount || 0} />
          </Col>
          <Col xs={12} md={6}>
            <Statistic
              title="Billed"
              value={summary.billed || summary.totalSales || 0}
              precision={2}
              prefix="₹"
            />
          </Col>
          <Col xs={12} md={6}>
            <Statistic
              title="Collected"
              value={summary.collected || summary.amountCollected || 0}
              precision={2}
              prefix="₹"
            />
          </Col>
          <Col xs={12} md={6}>
            <Statistic
              title="Expected cash"
              value={expectedCash}
              precision={2}
              prefix="₹"
            />
          </Col>
        </Row>

        {Array.isArray(byMethod) && byMethod.length > 0 && (
          <Table
            style={{ marginTop: 12 }}
            size="small"
            rowKey={(r: any) => String(r._id || r.method)}
            pagination={false}
            dataSource={byMethod}
            columns={[
              {
                title: "Method",
                render: (_: unknown, r: any) => {
                  const m = r._id || r.method;
                  return (
                    <Tag>{PAYMENT_METHOD_LABELS[m] || m || "—"}</Tag>
                  );
                },
              },
              {
                title: "Bills",
                dataIndex: "count",
                width: 80,
                align: "right" as const,
              },
              {
                title: "Collected",
                width: 120,
                align: "right" as const,
                render: (_: unknown, r: any) =>
                  formatCurrency(r.collected ?? r.amount ?? 0),
              },
            ]}
          />
        )}

        {!closed && (
          <Row gutter={12} style={{ marginTop: 16 }} align="bottom">
            <Col>
              <div style={{ fontSize: 12, marginBottom: 4 }}>Counted cash</div>
              <InputNumber
                min={0}
                style={{ width: 160 }}
                value={countedCash}
                onChange={setCountedCash}
                placeholder={String(expectedCash || 0)}
              />
            </Col>
            <Col flex="auto">
              <div style={{ fontSize: 12, marginBottom: 4 }}>Notes</div>
              <Input
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Optional notes"
              />
            </Col>
            <Col>
              <Button
                type="primary"
                danger
                icon={<LockOutlined />}
                loading={submitting}
                onClick={submit}
              >
                Close day
              </Button>
            </Col>
          </Row>
        )}
      </Card>
    </div>
  );
};

export default DayClosePanel;
